import { useEffect, useRef, useState } from "react";
import { ListVideo, Loader2, RefreshCw, SkipBack, SkipForward } from "lucide-react";
import { fmtClock } from "@/components/VideoBits";

export const chapterIndexAt = (chapters, t) => {
  let i = -1;
  for (let k = 0; k < chapters.length; k++) if (t >= chapters[k].start - 0.25) i = k; else break;
  return i;
};

const SKIPPABLE = [[/recap|previously/i, "Skip recap"], [/\bintro|opening|title sequence|theme/i, "Skip intro"]];
const CREDITS = /credits|\boutro\b|end titles/i;
export const skipLabel = (c) => (c ? SKIPPABLE.find(([re]) => re.test(c.title))?.[1] : null);

/* "Skip intro" / "Skip recap" / "Next episode" overlay — only for real chapter marks whose title says so, while the playhead is inside it. */
export const SkipButton = ({ chapters, source, time, onSeek, onNext }) => {
  if (source !== "embedded") return null;
  const cur = chapterIndexAt(chapters, time);
  const c = chapters[cur];
  if (!c) return null;
  const cls = "absolute bottom-20 right-6 z-10 flex items-center gap-1.5 rounded-md border border-white/40 bg-black/70 px-4 py-2 text-sm font-medium text-white backdrop-blur transition-colors hover:bg-white hover:text-black";
  if (onNext && CREDITS.test(c.title)) {
    return <button onClick={onNext} data-testid="video-next-episode" data-chapter={cur} className={cls}>Next episode<SkipForward size={14} /></button>;
  }
  const label = skipLabel(c);
  if (!label || c.end - time < 1) return null;
  return (
    <button onClick={() => onSeek(c.end)} data-testid="video-skip-chapter" data-chapter={cur} className={cls}>
      {label}<SkipForward size={14} />
    </button>
  );
};

/* Segmented strip under the player: one segment per chapter, played part filled, click to jump, hover for the name. */
export const ChapterStrip = ({ chapters, time, duration, onSeek }) => {
  const [hover, setHover] = useState(-1);
  const total = duration || chapters[chapters.length - 1]?.end || 1;
  const cur = chapterIndexAt(chapters, time);
  return (
    <div data-testid="chapter-strip">
      <div className="flex h-5 items-end gap-[3px]" onMouseLeave={() => setHover(-1)}>
        {chapters.map((c, i) => {
          const w = Math.max(0.5, ((c.end - c.start) / total) * 100);
          const fill = time <= c.start ? 0 : time >= c.end ? 100 : ((time - c.start) / (c.end - c.start)) * 100;
          return (
            <button key={i} style={{ width: `${w}%` }} onClick={() => onSeek(c.start)} onMouseEnter={() => setHover(i)} title={`${c.title} · ${fmtClock(c.start)}`}
              aria-label={`Jump to ${c.title}`} data-testid={`chapter-seg-${i}`} data-active={i === cur ? 1 : 0}
              className={`group relative h-[6px] overflow-hidden rounded-sm bg-zinc-200 transition-[height] hover:h-[10px] ${i === cur ? "ring-1 ring-zinc-900/40" : ""}`}>
              <span className="absolute inset-y-0 left-0 bg-zinc-900" style={{ width: `${fill}%` }} />
            </button>
          );
        })}
      </div>
      <p className="mt-1 h-4 truncate text-[11px] text-zinc-500" data-testid="chapter-strip-label">
        {hover >= 0 ? `${chapters[hover].title} · ${fmtClock(chapters[hover].start)}` : cur >= 0 ? `Ch. ${cur + 1} of ${chapters.length} · ${chapters[cur].title}` : ""}
      </p>
    </div>
  );
};

/* Prev / next chapter buttons for the action row. */
export const ChapterNav = ({ chapters, time, onSeek, btn, only = null }) => {
  const cur = chapterIndexAt(chapters, time);
  const prev = () => onSeek(cur > 0 && time - chapters[cur].start < 3 ? chapters[cur - 1].start : chapters[Math.max(cur, 0)].start);
  const next = () => { if (cur < chapters.length - 1) onSeek(chapters[cur + 1].start); };
  return (
    <>
      {only !== "next" && <button onClick={prev} className={btn} title="Previous chapter (Shift+←)" data-testid="chapter-prev"><SkipBack size={13} />Chapter</button>}
      {only !== "prev" && <button onClick={next} disabled={cur >= chapters.length - 1} className={btn + " disabled:opacity-40"} title="Next chapter (Shift+→)" data-testid="chapter-next">Chapter<SkipForward size={13} /></button>}
    </>
  );
};

/* Overlay list of chapters on the stage; active one highlighted and kept in view. */
export const ChapterPanel = ({ chapters, source, time, onSeek, onRefresh, canRefresh, onClose }) => {
  const cur = chapterIndexAt(chapters, time);
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { ref.current?.querySelector('[data-active="1"]')?.scrollIntoView({ block: "nearest" }); }, [cur]);
  const refresh = async () => { setBusy(true); try { await onRefresh(); } finally { setBusy(false); } };
  return (
    <div className="absolute bottom-16 right-4 top-4 z-10 flex w-80 max-w-[90%] flex-col rounded-xl bg-black/85 text-xs text-zinc-200 backdrop-blur" data-testid="chapter-panel" data-source={source}>
      <div className="flex items-center justify-between gap-2 px-4 pt-3">
        <p className="flex items-center gap-1.5 font-medium"><ListVideo size={13} />Chapters<span className="text-zinc-500">· {chapters.length}</span></p>
        <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="chapter-panel-close">Close</button>
      </div>
      <p className="px-4 pb-2 pt-0.5 text-[10px] text-zinc-400" data-testid="chapter-panel-source">
        {source === "embedded" ? "Chapter marks from the file" : chapters.length ? "No chapter marks in this file — generated every 10 minutes" : "No chapter marks in this file"}
      </p>
      <ol ref={ref} className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
        {chapters.map((c, i) => (
          <li key={i}>
            <button onClick={() => onSeek(c.start)} data-testid={`chapter-item-${i}`} data-active={i === cur ? 1 : 0}
              className={`flex w-full items-center gap-3 rounded-lg px-2 py-1.5 text-left hover:bg-white/10 ${i === cur ? "bg-white/15 text-white" : ""}`}>
              <span className="w-6 shrink-0 text-right font-mono text-[10px] text-zinc-500">{i + 1}</span>
              <span className="min-w-0 flex-1 truncate">{c.title}</span>
              <span className="shrink-0 font-mono text-[10px] text-zinc-400">{fmtClock(c.start)}</span>
            </button>
          </li>
        ))}
      </ol>
      {canRefresh && (
        <button onClick={refresh} disabled={busy} className="flex items-center justify-center gap-1.5 border-t border-white/10 px-4 py-2 text-[11px] text-zinc-300 hover:text-white disabled:opacity-50" data-testid="chapter-refresh">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}Re-read chapter marks
        </button>
      )}
    </div>
  );
};
